/**
 * Chart colours for the Major Analysis.
 *
 * SERIES is a colour-blind-checked categorical order (every adjacent pair
 * ΔE ≥ 9 under simulated CVD) — assign it in this fixed order by a player's
 * finishing position, never by what's currently shown, so a player keeps
 * their colour through every filter. Past eight players the rest share a
 * neutral grey and rely on their direct labels.
 *
 * Magnitudes that have a sign (strokes gained, score vs par) use the blue ↔
 * red diverging pair with a grey midpoint: blue = better, red = worse.
 */

export const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
export const SERIES_OVERFLOW = '#94a3b8';

export const seriesColor = (order: number) => (order >= 0 && order < SERIES.length ? SERIES[order] : SERIES_OVERFLOW);

const MID = '#f0efec';
const POS = ['#b7d3f6', '#5598e7', '#1c5cab']; // gained / under par
const NEG = ['#f6c2bf', '#e34948', '#a8262b']; // lost / over par

const hexToRgb = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgbToHex = (rgb: number[]) => `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
const mix = (a: string, b: string, t: number) => {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  return rgbToHex(x.map((v, i) => v + (y[i] - v) * t));
};

/** Diverging fill for a signed value: positive → blue, negative → red, 0 → grey. */
export function divergingColor(value: number, max: number): string {
  if (!Number.isFinite(value) || max <= 0 || value === 0) return MID;
  const t = Math.min(Math.abs(value) / max, 1);
  const ramp = value > 0 ? POS : NEG;
  // mid → light → mid step → dark, in three equal thirds.
  const stops = [MID, ...ramp];
  const seg = Math.min(Math.floor(t * 3), 2);
  return mix(stops[seg], stops[seg + 1], t * 3 - seg);
}

const luminance = (hex: string) => {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Ink that stays readable on a filled cell. */
export const inkOn = (fill: string) => (luminance(fill) > 0.36 ? '#0f172a' : '#ffffff');

/** Scoring mix: eagle+ … triple+, ordered around par. */
export const MIX_COLORS = {
  eagle: '#1c5cab',
  birdie: '#6da7ec',
  par: '#c3c2b7',
  bogey: '#f2a7a1',
  double: '#e34948',
  triple: '#a8262b',
} as const;

/** Putts per hole: 0, 1, 2, 3+. */
export const PUTT_COLORS = ['#1c5cab', '#6da7ec', '#c3c2b7', '#e34948'];
